import { useEffect, useState, useCallback } from 'react'
import { api } from '../lib/api'

interface LearningItem {
  id: number
  category: string
  title: string
  summary: string
  source_url: string | null
  created_at: string
}

const CATEGORY_EMOJI: Record<string, string> = {
  coding: '💻',
  security: '🔒',
  research: '🔬',
  mac: '🖥️',
  cocoon: '🧬',
}

const CATEGORY_COLOR: Record<string, string> = {
  coding: 'bg-blue-500/20 text-blue-300',
  security: 'bg-red-500/20 text-red-300',
  research: 'bg-purple-500/20 text-purple-300',
  mac: 'bg-green-500/20 text-green-300',
  cocoon: 'bg-amber-500/20 text-amber-300',
}

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr + 'Z').getTime()
  const diff = Math.floor((now - then) / 1000)

  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`
  return new Date(dateStr).toLocaleDateString()
}

export function LearningView() {
  const [items, setItems] = useState<LearningItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<string | null>(null)
  const [total, setTotal] = useState(0)

  const fetchLearning = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '30' })
      if (filter) params.set('category', filter)
      const res = await api.get(`/api/learning?${params}`)
      setItems(res.items)
      setTotal(res.total)
    } catch (e) {
      console.error('Failed to fetch learning feed:', e)
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    fetchLearning()
  }, [fetchLearning])

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <div className="spinner" />
        <p className="text-sm text-muted">Loading feed...</p>
      </div>
    )
  }

  return (
    <div className="px-4 space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-lg font-bold">🧠 Zen's Learning Feed</h2>
        <p className="text-xs text-muted mt-0.5">
          {total} thing{total !== 1 ? 's' : ''} learned and counting
        </p>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        <button
          onClick={() => setFilter(null)}
          className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
            !filter ? 'bg-white/15 text-white' : 'bg-white/5 text-muted'
          }`}
        >
          All
        </button>
        {Object.entries(CATEGORY_EMOJI).map(([cat, emoji]) => (
          <button
            key={cat}
            onClick={() => setFilter(filter === cat ? null : cat)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              filter === cat ? 'bg-white/15 text-white' : 'bg-white/5 text-muted'
            }`}
          >
            {emoji} {cat}
          </button>
        ))}
      </div>

      {/* Feed */}
      {items.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-4xl mb-3">📚</div>
          <p className="text-sm text-muted">No entries yet. Zen is warming up...</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item.id}
              className="rounded-2xl bg-white/5 border border-white/5 p-4 space-y-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLOR[item.category] || 'bg-white/10 text-white/70'}`}>
                    {CATEGORY_EMOJI[item.category] || '📝'} {item.category}
                  </span>
                </div>
                <span className="text-[10px] text-muted shrink-0">
                  {timeAgo(item.created_at)}
                </span>
              </div>
              <h3 className="text-sm font-semibold leading-snug">{item.title}</h3>
              <p className="text-xs text-muted leading-relaxed">{item.summary}</p>
              {item.source_url && (
                <a
                  href={item.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-accent-light hover:underline"
                >
                  🔗 Source
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
